/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useRef, useState } from "react";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap');

  :root {
    --lime: #B8FF3D;
    --navy: #0B0F17;
    --light-bg: #F4F6F8;
    --text-primary: #111827;
    --text-secondary: #6B7280;
  }

  * { margin: 0; padding: 0; box-sizing: border-box; }
  html { scroll-behavior: smooth; }

  body {
    font-family: 'Inter', sans-serif;
    background: var(--light-bg);
    color: var(--text-primary);
    overflow-x: hidden;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: 'Space Grotesk', sans-serif;
    letter-spacing: -0.02em;
    line-height: 1.1;
  }

  nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
    padding: 1rem 2rem;
    display: flex; justify-content: space-between; align-items: center;
    transition: all 0.3s ease;
  }
  nav.scrolled {
    background: rgba(255,255,255,0.9);
    backdrop-filter: blur(12px);
    box-shadow: 0 2px 20px rgba(0,0,0,0.05);
  }
  .logo {
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 600; font-size: 1.25rem;
    color: var(--text-primary); text-decoration: none;
  }
  .nav-links { display: flex; gap: 2rem; list-style: none; }
  .nav-links a {
    text-decoration: none; color: var(--text-secondary);
    font-size: 0.875rem; font-weight: 500; transition: color 0.2s;
  }
  .nav-links a:hover { color: var(--text-primary); }

  .btn-primary {
    display: inline-flex; align-items: center; gap: 0.5rem;
    padding: 0.75rem 1.5rem; background: var(--lime);
    color: var(--text-primary); font-weight: 500; font-size: 0.875rem;
    border-radius: 9999px; text-decoration: none;
    border: none; cursor: pointer; transition: all 0.2s;
    font-family: 'Inter', sans-serif;
  }
  .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(184,255,61,0.35); }

  .btn-secondary {
    display: inline-flex; align-items: center; gap: 0.5rem;
    padding: 0.75rem 1.5rem; background: white;
    color: var(--text-primary); font-weight: 500; font-size: 0.875rem;
    border-radius: 9999px; text-decoration: none;
    border: 1px solid rgba(0,0,0,0.08); cursor: pointer; transition: all 0.2s;
    font-family: 'Inter', sans-serif;
  }
  .btn-secondary:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.08); }

  .glass-card {
    background: rgba(255,255,255,0.72);
    border: 1px solid rgba(255,255,255,0.85);
    box-shadow: 0 18px 50px rgba(11,15,23,0.10);
    border-radius: 28px;
    backdrop-filter: blur(14px) saturate(110%);
  }
  .glass-card-dark {
    background: rgba(255,255,255,0.08);
    border: 1px solid rgba(255,255,255,0.15);
    box-shadow: 0 18px 50px rgba(0,0,0,0.30);
    border-radius: 28px;
    backdrop-filter: blur(14px) saturate(110%);
  }

  .title-pill {
    display: inline-flex; align-items: center; gap: 0.5rem;
    padding: 0.5rem 1rem; background: white; border-radius: 9999px;
    font-family: 'IBM Plex Mono', monospace; font-size: 0.6875rem;
    font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase;
    border: 1px solid rgba(0,0,0,0.08);
  }
  .title-pill-dark {
    display: inline-flex; align-items: center; gap: 0.5rem;
    padding: 0.5rem 1rem; background: rgba(255,255,255,0.1); border-radius: 9999px;
    font-family: 'IBM Plex Mono', monospace; font-size: 0.6875rem;
    font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase;
    color: white; border: 1px solid rgba(255,255,255,0.15);
  }
  .micro-label {
    font-family: 'IBM Plex Mono', monospace; font-size: 0.6875rem;
    font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase;
    color: var(--text-secondary);
  }

  .section {
    min-height: 100vh; position: relative;
    display: flex; align-items: center; justify-content: center; overflow: hidden;
  }
  .section-bg { position: absolute; inset: 0; z-index: 0; }
  .section-bg img { width: 100%; height: 100%; object-fit: cover; }
  .section-bg::after { content: ''; position: absolute; inset: 0; background: rgba(0,0,0,0.1); }
  .section-content { position: relative; z-index: 1; width: 100%; max-width: 1200px; padding: 2rem; }

  .hero-card { max-width: 900px; margin: 0 auto; padding: 3rem; text-align: center; }
  .hero-card h1 { font-size: clamp(2rem,5vw,3.5rem); font-weight: 600; margin: 1.5rem 0; color: var(--text-primary); }
  .hero-card p { font-size: 1.125rem; color: var(--text-secondary); line-height: 1.7; max-width: 600px; margin: 0 auto 2rem; }
  .hero-buttons { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }

  .feature-split { display: grid; grid-template-columns: 1fr 1fr; gap: 3rem; align-items: center; }
  .feature-card { padding: 2.5rem; }
  .feature-card h2 { font-size: clamp(1.75rem,4vw,2.75rem); font-weight: 600; margin: 1rem 0 1.5rem; color: var(--text-primary); }
  .feature-card p { color: var(--text-secondary); line-height: 1.7; margin-bottom: 1.5rem; }
  .feature-image { border-radius: 24px; overflow: hidden; box-shadow: 0 18px 50px rgba(11,15,23,0.10); height: 500px; }
  .feature-image img { width: 100%; height: 100%; object-fit: cover; }

  .flowing-section { padding: 6rem 2rem; max-width: 1200px; margin: 0 auto; }
  .section-header { margin-bottom: 3rem; }
  .section-header h2 { font-size: clamp(2rem,4vw,3rem); font-weight: 600; margin-bottom: 1rem; }
  .section-header p { color: var(--text-secondary); max-width: 500px; line-height: 1.7; }

  .steps { display: flex; flex-direction: column; gap: -2rem; }
  .step-card {
    display: grid; grid-template-columns: 1fr auto; gap: 2rem;
    padding: 2rem; margin-bottom: -2rem; position: relative;
  }
  .step-card:nth-child(1) { margin-left: 0; }
  .step-card:nth-child(2) { margin-left: 4rem; }
  .step-card:nth-child(3) { margin-left: 8rem; }
  .step-number { font-family: 'IBM Plex Mono', monospace; font-size: 0.875rem; color: var(--text-secondary); margin-bottom: 0.5rem; }
  .step-icon {
    width: 40px; height: 40px; background: rgba(184,255,61,0.2);
    border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-bottom: 1rem;
  }
  .step-card h3 { font-size: 1.5rem; font-weight: 600; margin-bottom: 0.5rem; }
  .step-card p { color: var(--text-secondary); line-height: 1.6; font-size: 0.9375rem; }
  .step-image { width: 200px; height: 150px; border-radius: 16px; overflow: hidden; }
  .step-image img { width: 100%; height: 100%; object-fit: cover; }

  .partner-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 1.5rem; }
  .partner-card { padding: 1.5rem; }
  .partner-card-header { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.75rem; }
  .partner-icon {
    width: 36px; height: 36px; background: rgba(184,255,61,0.2);
    border-radius: 50%; display: flex; align-items: center; justify-content: center;
  }
  .partner-card p { color: var(--text-secondary); font-size: 0.875rem; margin-bottom: 1rem; }
  .partner-tags { display: flex; flex-wrap: wrap; gap: 0.5rem; }
  .partner-tag {
    padding: 0.375rem 0.875rem; background: white;
    border: 1px solid rgba(0,0,0,0.06); border-radius: 8px;
    font-size: 0.8125rem; color: var(--text-secondary);
  }

  .value-cards { display: flex; flex-direction: column; gap: -2rem; }
  .value-card { padding: 2rem; margin-bottom: -2rem; display: flex; gap: 1.5rem; }
  .value-card:nth-child(1) { margin-left: 0; }
  .value-card:nth-child(2) { margin-left: 3rem; }
  .value-card:nth-child(3) { margin-left: 6rem; }
  .value-icon {
    width: 48px; height: 48px; background: rgba(184,255,61,0.2);
    border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .value-card h3 { font-size: 1.375rem; font-weight: 600; margin-bottom: 0.5rem; }
  .value-card p { color: var(--text-secondary); line-height: 1.6; margin-bottom: 1rem; }
  .value-features { list-style: none; }
  .value-features li { display: flex; align-items: center; gap: 0.5rem; font-size: 0.875rem; margin-bottom: 0.375rem; }
  .value-features li::before { content: '✓'; color: var(--lime); font-weight: 600; }

  .cta-card { max-width: 800px; margin: 0 auto; padding: 3rem; text-align: center; }
  .cta-card h2 { font-size: clamp(2rem,5vw,3rem); font-weight: 600; margin: 1.5rem 0; color: white; }
  .cta-card p { font-size: 1.125rem; color: rgba(255,255,255,0.7); line-height: 1.7; max-width: 600px; margin: 0 auto 2rem; }
  .cta-buttons { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }
  .btn-outline {
    display: inline-flex; align-items: center; gap: 0.5rem;
    padding: 0.75rem 1.5rem; background: rgba(255,255,255,0.1);
    color: white; font-weight: 500; font-size: 0.875rem;
    border-radius: 9999px; text-decoration: none;
    border: 1px solid rgba(255,255,255,0.2); cursor: pointer; transition: all 0.2s;
    font-family: 'Inter', sans-serif;
  }
  .btn-outline:hover { background: rgba(255,255,255,0.2); }

  footer { background: var(--navy); padding: 4rem 2rem 2rem; }
  .footer-accent { height: 2px; background: linear-gradient(90deg,transparent,var(--lime),transparent); margin-bottom: 3rem; }
  .footer-grid { display: grid; grid-template-columns: 1fr 1.5fr; gap: 4rem; max-width: 1200px; margin: 0 auto; }
  .footer-form h3 { color: white; font-size: 1.5rem; margin-bottom: 0.5rem; }
  .footer-form p { color: rgba(255,255,255,0.6); font-size: 0.875rem; margin-bottom: 1.5rem; }
  .form-group { margin-bottom: 1rem; }
  .form-group input, .form-group textarea, .form-group select {
    width: 100%; padding: 0.875rem 1rem;
    background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
    border-radius: 12px; color: white; font-size: 0.875rem; outline: none; transition: border-color 0.2s;
    font-family: 'Inter', sans-serif;
  }
  .form-group input::placeholder, .form-group textarea::placeholder { color: rgba(255,255,255,0.4); }
  .form-group input:focus, .form-group textarea:focus, .form-group select:focus { border-color: rgba(184,255,61,0.5); }
  .form-group textarea { resize: none; min-height: 100px; }
  .form-group select option { background: var(--navy); color: white; }
  .footer-info h4 { color: white; font-size: 1.125rem; margin-bottom: 1rem; }
  .footer-info p { color: rgba(255,255,255,0.6); font-size: 0.875rem; line-height: 1.7; max-width: 400px; margin-bottom: 1.5rem; }
  .footer-links { display: flex; flex-wrap: wrap; gap: 0.75rem; margin-bottom: 2rem; }
  .footer-link {
    display: inline-flex; align-items: center; gap: 0.5rem;
    padding: 0.5rem 1rem; background: rgba(255,255,255,0.05);
    border-radius: 8px; color: rgba(255,255,255,0.7); font-size: 0.8125rem;
    text-decoration: none; transition: all 0.2s;
  }
  .footer-link:hover { background: rgba(255,255,255,0.1); color: white; }
  .footer-copyright {
    color: rgba(255,255,255,0.4); font-size: 0.8125rem;
    padding-top: 2rem; border-top: 1px solid rgba(255,255,255,0.1);
  }

  .noise {
    position: fixed; top: 0; left: 0; width: 100%; height: 100%;
    pointer-events: none; z-index: 9999; opacity: 0.04; mix-blend-mode: multiply;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
  }

  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-in { animation: fadeInUp 0.8s ease forwards; }
  .delay-1 { animation-delay: 0.1s; }
  .delay-2 { animation-delay: 0.2s; }
  .delay-3 { animation-delay: 0.3s; }

  /* ── LAUNCH BANNER SECTION ── */
  .launch-section {
    position: relative;
    padding: 0;
    overflow: hidden;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 40%, #f093fb 100%);
    min-height: 340px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .launch-section::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.04'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    pointer-events: none;
  }

  .launch-inner {
    position: relative;
    z-index: 2;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 2rem;
    max-width: 1200px;
    width: 100%;
    padding: 4rem 2rem;
    align-items: center;
  }

  .launch-card {
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.22);
    border-radius: 28px;
    backdrop-filter: blur(16px) saturate(120%);
    padding: 2.5rem 2rem;
    text-align: center;
    transition: transform 0.4s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.4s ease;
    cursor: default;
    position: relative;
    overflow: hidden;
  }
  .launch-card::after {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent);
  }
  .launch-card:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 32px 80px rgba(0,0,0,0.25);
  }

  .launch-card-main {
    background: rgba(255,255,255,0.92);
    border: 1px solid rgba(255,255,255,1);
    border-radius: 32px;
    backdrop-filter: blur(20px);
    padding: 3rem 2.5rem;
    text-align: center;
    position: relative;
    overflow: hidden;
    transition: transform 0.4s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.4s ease;
    box-shadow: 0 24px 60px rgba(0,0,0,0.18);
  }
  .launch-card-main:hover {
    transform: translateY(-6px);
    box-shadow: 0 40px 100px rgba(0,0,0,0.28);
  }
  .launch-card-main::before {
    content: '';
    position: absolute;
    top: -60px; left: -60px;
    width: 160px; height: 160px;
    background: radial-gradient(circle, rgba(102,126,234,0.12) 0%, transparent 70%);
    border-radius: 50%;
  }
  .launch-title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.75rem;
    font-weight: 700;
    color: #111827;
    margin-bottom: 0.5rem;
    letter-spacing: -0.03em;
  }
  .launch-subtitle {
    color: #6B7280;
    font-size: 1rem;
    line-height: 1.5;
    margin-bottom: 1.5rem;
  }
  .launch-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1.25rem;
    background: linear-gradient(135deg, #667eea, #764ba2);
    border-radius: 9999px;
    color: white;
    font-family: 'IBM Plex Mono', monospace;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    margin-bottom: 1.5rem;
    box-shadow: 0 4px 20px rgba(102,126,234,0.4);
  }
  .launch-countdown {
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin: 1.5rem 0;
  }
  .countdown-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    background: rgba(102,126,234,0.08);
    border: 1px solid rgba(102,126,234,0.15);
    border-radius: 16px;
    padding: 0.875rem 1rem;
    min-width: 60px;
    transition: background 0.3s ease;
  }
  .countdown-block:hover { background: rgba(102,126,234,0.14); }
  .countdown-num {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 1.75rem;
    font-weight: 700;
    color: #764ba2;
    line-height: 1;
    margin-bottom: 0.25rem;
  }
  .countdown-label {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 0.5625rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #9CA3AF;
  }
  .launch-side-card-title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.125rem;
    font-weight: 600;
    color: white;
    margin-bottom: 0.5rem;
  }
  .launch-side-card-text {
    color: rgba(255,255,255,0.75);
    font-size: 0.875rem;
    line-height: 1.6;
    margin-bottom: 1.25rem;
  }
  .launch-feat-list { list-style: none; }
  .launch-feat-list li {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    color: rgba(255,255,255,0.85);
    font-size: 0.8125rem;
    padding: 0.375rem 0;
  }
  .launch-feat-list li::before { content: '✦'; color: #f093fb; font-size: 0.5rem; }

  .launch-icon-wrap {
    width: 52px;
    height: 52px;
    background: rgba(255,255,255,0.15);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    margin: 0 auto 1rem;
    border: 1px solid rgba(255,255,255,0.2);
  }

  .launch-notify-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.75rem;
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    font-weight: 600;
    font-size: 0.875rem;
    border-radius: 9999px;
    border: none;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.34,1.56,0.64,1);
    font-family: 'Inter', sans-serif;
    box-shadow: 0 4px 20px rgba(102,126,234,0.4);
  }
  .launch-notify-btn:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 8px 32px rgba(102,126,234,0.55);
  }

  .confetti-piece {
    position: absolute;
    width: 8px;
    height: 8px;
    opacity: 0;
    border-radius: 2px;
    animation: confettiFall linear infinite;
  }
  @keyframes confettiFall {
    0% { opacity: 1; transform: translateY(-40px) rotate(0deg); }
    100% { opacity: 0; transform: translateY(360px) rotate(720deg); }
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.6; transform: scale(1.3); }
  }

  /* scroll-reveal utility */
  .reveal {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .reveal.visible {
    opacity: 1;
    transform: translateY(0);
  }
  .reveal-left {
    opacity: 0;
    transform: translateX(-40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .reveal-left.visible { opacity: 1; transform: translateX(0); }
  .reveal-right {
    opacity: 0;
    transform: translateX(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .reveal-right.visible { opacity: 1; transform: translateX(0); }
  .reveal-scale {
    opacity: 0;
    transform: scale(0.92);
    transition: opacity 0.7s ease, transform 0.7s cubic-bezier(0.34,1.56,0.64,1);
  }
  .reveal-scale.visible { opacity: 1; transform: scale(1); }

  .td-0 { transition-delay: 0s; }
  .td-1 { transition-delay: 0.1s; }
  .td-2 { transition-delay: 0.2s; }
  .td-3 { transition-delay: 0.3s; }
  .td-4 { transition-delay: 0.4s; }
  .td-5 { transition-delay: 0.5s; }

  @media (max-width: 968px) {
    .launch-inner { grid-template-columns: 1fr; }
  }

  /* ── DEMO LANDING MODAL ── */
  .demo-overlay {
    position: fixed; inset: 0; z-index: 2000;
    background: rgba(11,15,23,0.85); backdrop-filter: blur(10px);
    display: flex; align-items: center; justify-content: center; padding: 1rem;
    animation: fadeInOverlay 0.3s ease;
  }
  @keyframes fadeInOverlay { from { opacity: 0; } to { opacity: 1; } }
  .demo-modal {
    background: white; border-radius: 32px; width: 100%; max-width: 900px;
    overflow: hidden; box-shadow: 0 40px 120px rgba(0,0,0,0.5);
    animation: slideUpModal 0.4s cubic-bezier(0.22,1,0.36,1);
    display: grid; grid-template-columns: 1fr 1fr;
  }
  @keyframes slideUpModal { from { opacity: 0; transform: translateY(40px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
  .demo-left {
    background: var(--navy); padding: 3rem;
    display: flex; flex-direction: column; justify-content: space-between;
  }
  .demo-logo { font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 1.5rem; color: white; margin-bottom: 2rem; }
  .demo-logo span { color: var(--lime); }
  .demo-left h2 { font-size: 1.75rem; color: white; margin-bottom: 1rem; line-height: 1.2; }
  .demo-left p { color: rgba(255,255,255,0.6); font-size: 0.9375rem; line-height: 1.7; margin-bottom: 2rem; }
  .demo-features { list-style: none; display: flex; flex-direction: column; gap: 0.75rem; }
  .demo-features li { display: flex; align-items: center; gap: 0.75rem; color: rgba(255,255,255,0.85); font-size: 0.875rem; }
  .demo-check { width: 22px; height: 22px; background: rgba(184,255,61,0.15); border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 0.625rem; color: var(--lime); }
  .demo-right { padding: 3rem; display: flex; flex-direction: column; }
  .demo-right h3 { font-size: 1.375rem; font-weight: 600; margin-bottom: 0.5rem; }
  .demo-right > p { color: var(--text-secondary); font-size: 0.875rem; margin-bottom: 2rem; }
  .demo-close {
    position: absolute; top: 1.5rem; right: 1.5rem;
    width: 36px; height: 36px; background: rgba(255,255,255,0.1);
    border: 1px solid rgba(255,255,255,0.15); border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; color: white; font-size: 1rem; transition: all 0.2s;
  }
  .demo-close:hover { background: rgba(255,255,255,0.2); }
  .demo-form-group { margin-bottom: 1rem; }
  .demo-form-group label { display: block; font-size: 0.8125rem; font-weight: 500; margin-bottom: 0.375rem; color: var(--text-primary); }
  .demo-form-group input, .demo-form-group select {
    width: 100%; padding: 0.75rem 1rem;
    background: #F9FAFB; border: 1px solid rgba(0,0,0,0.1);
    border-radius: 12px; font-size: 0.875rem; outline: none; transition: border-color 0.2s;
    font-family: 'Inter', sans-serif; color: var(--text-primary);
  }
  .demo-form-group input:focus, .demo-form-group select:focus { border-color: rgba(184,255,61,0.6); box-shadow: 0 0 0 3px rgba(184,255,61,0.12); }
  .demo-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  .demo-submit {
    margin-top: 1rem; width: 100%; padding: 0.875rem;
    background: var(--lime); color: var(--text-primary);
    border: none; border-radius: 12px; font-size: 0.9375rem; font-weight: 600;
    cursor: pointer; transition: all 0.2s; font-family: 'Inter', sans-serif;
  }
  .demo-submit:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(184,255,61,0.4); }
  .demo-skip { margin-top: 1rem; text-align: center; font-size: 0.8125rem; color: var(--text-secondary); }
  .demo-skip button { background: none; border: none; cursor: pointer; color: var(--text-secondary); text-decoration: underline; font-size: 0.8125rem; font-family: 'Inter', sans-serif; }
  .demo-steps { display: flex; gap: 0.5rem; margin-bottom: 2rem; }
  .demo-step-dot { height: 4px; border-radius: 2px; flex: 1; background: #E5E7EB; transition: background 0.3s; }
  .demo-step-dot.active { background: var(--lime); }

  /* ── INTERACTIVE DEMO SECTION ── */
  .demo-section { padding: 6rem 2rem 2.5rem; background: var(--navy); }
  .demo-section-inner { max-width: 1200px; margin: 0 auto; }
  .demo-section-header { text-align: center; margin-bottom: 3rem; }
  .demo-section-header h2 { font-size: clamp(2rem,4vw,3rem); font-weight: 600; color: white; margin-bottom: 1rem; }
  .demo-section-header p { color: rgba(255,255,255,0.6); max-width: 500px; margin: 0 auto; line-height: 1.7; }

  .demo-tabs { display: flex; gap: 0.5rem; justify-content: center; margin-bottom: 3rem; flex-wrap: wrap; }
  .demo-tab {
    padding: 0.625rem 1.25rem; border-radius: 9999px; font-size: 0.875rem; font-weight: 500;
    border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.05);
    color: rgba(255,255,255,0.6); cursor: pointer; transition: all 0.2s; font-family: 'Inter', sans-serif;
  }
  .demo-tab.active { background: var(--lime); color: var(--navy); border-color: var(--lime); font-weight: 600; }
  .demo-tab:hover:not(.active) { background: rgba(255,255,255,0.1); color: white; }

  .demo-screen {
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1);
    border-radius: 24px; overflow: hidden; padding: 2rem;
    animation: fadeInUp 0.4s ease;
  }
  .demo-screen-header { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1.5rem; }
  .demo-dot { width: 10px; height: 10px; border-radius: 50%; }
  .demo-stat-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 1rem; margin-bottom: 1.5rem; }
  .demo-stat {
    background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08);
    border-radius: 16px; padding: 1.25rem;
  }
  .demo-stat-label { font-size: 0.75rem; color: rgba(255,255,255,0.5); margin-bottom: 0.5rem; font-family: 'IBM Plex Mono', monospace; letter-spacing: 0.08em; text-transform: uppercase; }
  .demo-stat-value { font-size: 1.75rem; font-weight: 700; color: white; font-family: 'Space Grotesk', sans-serif; }
  .demo-stat-change { font-size: 0.75rem; color: var(--lime); margin-top: 0.25rem; }
  .demo-chart-bar-wrap { display: flex; gap: 0.5rem; align-items: flex-end; height: 120px; margin-bottom: 1.5rem; }
  .demo-bar { flex: 1; border-radius: 6px 6px 0 0; transition: all 0.3s; position: relative; }
  .demo-bar:hover { opacity: 0.85; cursor: pointer; }
  .demo-table { width: 100%; border-collapse: collapse; }
  .demo-table th { text-align: left; font-size: 0.75rem; color: rgba(255,255,255,0.4); font-family: 'IBM Plex Mono', monospace; letter-spacing: 0.08em; text-transform: uppercase; padding: 0.5rem 1rem; border-bottom: 1px solid rgba(255,255,255,0.08); }
  .demo-table td { padding: 0.875rem 1rem; font-size: 0.875rem; color: rgba(255,255,255,0.8); border-bottom: 1px solid rgba(255,255,255,0.04); }
  .demo-badge { display: inline-flex; align-items: center; gap: 0.375rem; padding: 0.25rem 0.75rem; border-radius: 9999px; font-size: 0.75rem; font-weight: 500; }
  .badge-active { background: rgba(184,255,61,0.15); color: var(--lime); }
  .badge-pending { background: rgba(251,191,36,0.15); color: #FBBF24; }
  .badge-retired { background: rgba(99,102,241,0.15); color: #818CF8; }

  /* ── CONTACT US SECTION (REDESIGNED) ── */
  .contact-section-wrap {
    position: relative;
    overflow: hidden;
    background: linear-gradient(160deg, #0B0F17 0%, #111827 60%, #0f1a10 100%);
    padding: 7rem 2rem;
  }
  .contact-section-wrap::before {
    content: '';
    position: absolute;
    top: -120px; left: -120px;
    width: 500px; height: 500px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(184,255,61,0.07) 0%, transparent 70%);
    pointer-events: none;
  }
  .contact-section-wrap::after {
    content: '';
    position: absolute;
    bottom: -80px; right: -80px;
    width: 400px; height: 400px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(102,126,234,0.06) 0%, transparent 70%);
    pointer-events: none;
  }
  .contact-section { max-width: 1200px; margin: 0 auto; position: relative; z-index: 1; }
  .contact-header { text-align: center; margin-bottom: 4rem; }
  .contact-header h2 { font-size: clamp(2.25rem,5vw,3.5rem); font-weight: 700; color: white; margin: 1rem 0 0.75rem; letter-spacing: -0.04em; }
  .contact-header p { color: rgba(255,255,255,0.55); font-size: 1.125rem; line-height: 1.7; max-width: 520px; margin: 0 auto; }

  .contact-grid { display: grid; grid-template-columns: 1fr 1.6fr; gap: 3rem; align-items: start; }

  /* Info side */
  .contact-info { display: flex; flex-direction: column; gap: 1.5rem; }
  .contact-method {
    display: flex; align-items: flex-start; gap: 1.25rem;
    padding: 1.5rem; border-radius: 20px;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    transition: all 0.35s cubic-bezier(0.34,1.56,0.64,1);
    cursor: default;
    position: relative;
    overflow: hidden;
  }
  .contact-method::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(184,255,61,0.04), transparent);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  .contact-method:hover {
    transform: translateX(6px);
    border-color: rgba(184,255,61,0.25);
    box-shadow: 0 8px 32px rgba(0,0,0,0.2), -4px 0 0 var(--lime);
  }
  .contact-method:hover::before { opacity: 1; }
  .contact-method-icon {
    width: 48px; height: 48px; flex-shrink: 0;
    background: rgba(184,255,61,0.1);
    border: 1px solid rgba(184,255,61,0.2);
    border-radius: 14px;
    display: flex; align-items: center; justify-content: center;
    font-size: 1.375rem;
    transition: all 0.3s ease;
  }
  .contact-method:hover .contact-method-icon {
    background: rgba(184,255,61,0.18);
    transform: scale(1.1) rotate(-4deg);
  }
  .contact-method h4 { font-size: 1rem; font-weight: 600; color: white; margin-bottom: 0.3rem; }
  .contact-method p { font-size: 0.8125rem; color: rgba(255,255,255,0.55); line-height: 1.55; }
  .contact-method a { color: var(--lime); text-decoration: none; font-weight: 500; transition: opacity 0.2s; }
  .contact-method a:hover { opacity: 0.75; }

  .contact-stat-strip {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem;
    margin-top: 0.5rem;
  }
  .contact-stat-box {
    background: rgba(184,255,61,0.06);
    border: 1px solid rgba(184,255,61,0.12);
    border-radius: 16px; padding: 1.25rem 1rem; text-align: center;
    transition: all 0.3s ease;
  }
  .contact-stat-box:hover {
    background: rgba(184,255,61,0.11);
    transform: translateY(-3px);
    box-shadow: 0 12px 30px rgba(0,0,0,0.2);
  }
  .contact-stat-num { font-family: 'IBM Plex Mono', monospace; font-size: 1.5rem; font-weight: 700; color: var(--lime); }
  .contact-stat-lbl { font-size: 0.6875rem; color: rgba(255,255,255,0.45); font-family: 'IBM Plex Mono', monospace; letter-spacing: 0.1em; text-transform: uppercase; margin-top: 0.25rem; }

  /* Form side */
  .contact-form-card {
    background: rgba(255,255,255,0.035);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 28px; padding: 2.75rem;
    backdrop-filter: blur(16px);
    box-shadow: 0 32px 80px rgba(0,0,0,0.35);
    position: relative; overflow: hidden;
  }
  .contact-form-card::before {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(184,255,61,0.4), transparent);
  }
  .contact-form-card h3 { font-size: 1.5rem; font-weight: 700; color: white; margin-bottom: 0.4rem; }
  .contact-form-card > p { color: rgba(255,255,255,0.5); font-size: 0.9rem; margin-bottom: 2rem; line-height: 1.6; }

  .contact-field { margin-bottom: 1.25rem; position: relative; }
  .contact-field label {
    display: block; font-size: 0.8rem; font-weight: 500; margin-bottom: 0.4rem;
    color: rgba(255,255,255,0.65); letter-spacing: 0.02em;
    transition: color 0.2s;
  }
  .contact-field:focus-within label { color: var(--lime); }
  .contact-field input, .contact-field textarea, .contact-field select {
    width: 100%; padding: 0.9rem 1.1rem;
    background: rgba(255,255,255,0.05);
    border: 1.5px solid rgba(255,255,255,0.1);
    border-radius: 14px; font-size: 0.9375rem; outline: none;
    transition: all 0.3s cubic-bezier(0.4,0,0.2,1);
    font-family: 'Inter', sans-serif; color: white;
  }
  .contact-field input::placeholder, .contact-field textarea::placeholder { color: rgba(255,255,255,0.3); }
  .contact-field select option { background: #111827; color: white; }
  .contact-field input:focus, .contact-field textarea:focus, .contact-field select:focus {
    border-color: var(--lime);
    background: rgba(184,255,61,0.05);
    box-shadow: 0 0 0 4px rgba(184,255,61,0.1);
    transform: translateY(-1px);
  }
  .contact-field textarea { resize: none; min-height: 130px; }
  .contact-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }

  .contact-topic-pills { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.5rem; }
  .contact-topic-pill {
    padding: 0.45rem 1rem; border-radius: 9999px; font-size: 0.8rem; font-weight: 500;
    border: 1.5px solid rgba(255,255,255,0.12);
    background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.6);
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.34,1.56,0.64,1);
    font-family: 'Inter', sans-serif;
  }
  .contact-topic-pill:hover { border-color: rgba(184,255,61,0.4); color: rgba(255,255,255,0.9); transform: translateY(-2px); }
  .contact-topic-pill.active {
    background: rgba(184,255,61,0.15); border-color: var(--lime);
    color: var(--lime); font-weight: 600; transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(184,255,61,0.2);
  }

  .contact-submit {
    width: 100%; padding: 1.1rem; margin-top: 0.5rem;
    background: var(--lime); color: var(--navy);
    border: none; border-radius: 14px; font-size: 1rem; font-weight: 700;
    cursor: pointer; font-family: 'Inter', sans-serif;
    display: flex; align-items: center; justify-content: center; gap: 0.5rem;
    transition: all 0.35s cubic-bezier(0.34,1.56,0.64,1);
    position: relative; overflow: hidden;
  }
  .contact-submit::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(135deg, rgba(255,255,255,0.2), transparent);
    opacity: 0; transition: opacity 0.3s;
  }
  .contact-submit:hover {
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 12px 36px rgba(184,255,61,0.45);
  }
  .contact-submit:hover::after { opacity: 1; }
  .contact-submit:active { transform: translateY(0) scale(0.98); }

  /* Success state */
  .contact-success {
    text-align: center; padding: 3rem 1rem;
    display: flex; flex-direction: column; align-items: center; gap: 1.25rem;
    animation: fadeInUp 0.6s cubic-bezier(0.22,1,0.36,1);
  }
  .contact-success-icon {
    width: 80px; height: 80px;
    background: rgba(184,255,61,0.12);
    border: 2px solid rgba(184,255,61,0.3);
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center; font-size: 2.5rem;
    animation: successPop 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.2s both;
  }
  @keyframes successPop { from { transform: scale(0) rotate(-10deg); opacity: 0; } to { transform: scale(1) rotate(0); opacity: 1; } }
  .contact-success h3 { font-size: 1.75rem; font-weight: 700; color: white; }
  .contact-success p { color: rgba(255,255,255,0.6); line-height: 1.7; max-width: 300px; }

  /* Extra transition upgrades */
  .nav-links a { position: relative; }
  .nav-links a::after {
    content: '';
    position: absolute; bottom: -2px; left: 0; right: 0; height: 1.5px;
    background: var(--lime); border-radius: 1px;
    transform: scaleX(0); transform-origin: right;
    transition: transform 0.3s cubic-bezier(0.4,0,0.2,1);
  }
  .nav-links a:hover::after { transform: scaleX(1); transform-origin: left; }

  .step-card { transition: transform 0.4s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.4s ease; }
  .step-card:hover { transform: translateY(-6px) translateX(4px); box-shadow: 0 24px 60px rgba(0,0,0,0.14); }

  .partner-card { transition: transform 0.4s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.4s ease; }
  .partner-card:hover { transform: translateY(-8px); box-shadow: 0 24px 60px rgba(0,0,0,0.12); }

  .value-card { transition: transform 0.4s ease, box-shadow 0.4s ease; }
  .value-card:hover { transform: translateX(8px); box-shadow: 0 20px 50px rgba(0,0,0,0.1); }

  .testimonial-card { transition: all 0.35s cubic-bezier(0.4,0,0.2,1); }
  .testimonial-card:hover { transform: translateY(-4px); border-color: rgba(184,255,61,0.2); box-shadow: 0 16px 40px rgba(0,0,0,0.3); }

  .demo-tab { transition: all 0.3s cubic-bezier(0.34,1.56,0.64,1); }
  .demo-stat { transition: all 0.3s cubic-bezier(0.34,1.56,0.64,1); }
  .demo-stat:hover { transform: translateY(-4px); border-color: rgba(184,255,61,0.2); }

  .feature-image { transition: transform 0.6s ease, box-shadow 0.6s ease; overflow: hidden; }
  .feature-image:hover { transform: scale(1.02); box-shadow: 0 30px 80px rgba(0,0,0,0.2); }
  .feature-image img { transition: transform 0.8s ease; }
  .feature-image:hover img { transform: scale(1.05); }

  .partner-tag { transition: all 0.25s ease; cursor: default; }
  .partner-tag:hover { background: rgba(184,255,61,0.12); color: var(--text-primary); transform: translateY(-1px); border-color: rgba(184,255,61,0.3); }

  @keyframes shimmer {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  .btn-primary {
    background: var(--lime);
    transition: all 0.35s cubic-bezier(0.34,1.56,0.64,1);
  }
  .btn-primary:hover {
    transform: translateY(-3px) scale(1.04);
    box-shadow: 0 12px 32px rgba(184,255,61,0.45);
  }

  /* ── CONTACT V2 SECTION ── */
  .contact-v2-section {
    background: linear-gradient(160deg, #0a0e16 0%, #0f1923 55%, #0b150d 100%);
    padding: 2.5rem 2rem 7rem;
    position: relative; overflow: hidden;
  }
  .contact-v2-section::before {
    content: ''; position: absolute; top: -100px; left: -100px;
    width: 500px; height: 500px; border-radius: 50%;
    background: radial-gradient(circle, rgba(184,255,61,0.055) 0%, transparent 70%);
    pointer-events: none;
  }
  .contact-v2-section::after {
    content: ''; position: absolute; bottom: -80px; right: -80px;
    width: 380px; height: 380px; border-radius: 50%;
    background: radial-gradient(circle, rgba(102,126,234,0.05) 0%, transparent 70%);
    pointer-events: none;
  }
  .contact-v2-inner { max-width: 1200px; margin: 0 auto; position: relative; z-index: 1; }
  .contact-v2-header { text-align: center; margin-bottom: 4.5rem; }
  .contact-v2-header h2 {
    font-size: clamp(2.25rem, 5vw, 3.5rem); font-weight: 700; color: white;
    margin: 1rem 0 0.75rem; letter-spacing: -0.04em; line-height: 1.1;
  }
  .contact-v2-header p { color: rgba(255,255,255,0.5); font-size: 1.125rem; line-height: 1.7; max-width: 520px; margin: 0 auto; }
  .contact-v2-grid { display: grid; grid-template-columns: 1fr 1.7fr; gap: 3.5rem; align-items: start; }

  /* Info column */
  .contact-v2-info { display: flex; flex-direction: column; gap: 1rem; }
  .contact-info-card {
    display: flex; align-items: flex-start; gap: 1.25rem;
    padding: 1.5rem; border-radius: 20px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.07);
    transition: all 0.4s cubic-bezier(0.34,1.56,0.64,1);
    position: relative; overflow: hidden; cursor: default;
  }
  .contact-info-card::before {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(135deg, rgba(184,255,61,0.04), transparent);
    opacity: 0; transition: opacity 0.3s ease;
  }
  .contact-info-card:hover {
    transform: translateX(8px);
    border-color: rgba(184,255,61,0.22);
    box-shadow: 0 8px 32px rgba(0,0,0,0.2), -3px 0 0 var(--lime);
  }
  .contact-info-card:hover::before { opacity: 1; }
  .contact-info-icon {
    width: 46px; height: 46px; flex-shrink: 0;
    background: rgba(184,255,61,0.09);
    border: 1px solid rgba(184,255,61,0.18);
    border-radius: 14px;
    display: flex; align-items: center; justify-content: center; font-size: 1.35rem;
    transition: all 0.35s cubic-bezier(0.34,1.56,0.64,1);
  }
  .contact-info-card:hover .contact-info-icon { transform: scale(1.1) rotate(-5deg); background: rgba(184,255,61,0.15); }
  .contact-info-title { font-size: 0.9375rem; font-weight: 600; color: white; margin-bottom: 0.3rem; }
  .contact-info-desc { font-size: 0.8125rem; color: rgba(255,255,255,0.5); line-height: 1.55; margin-bottom: 0.4rem; }
  .contact-info-link { font-size: 0.8125rem; color: var(--lime); text-decoration: none; font-weight: 500; transition: opacity 0.2s; }
  .contact-info-link:hover { opacity: 0.7; }

  .contact-social-bar {
    margin-top: 0.5rem;
    padding: 1.25rem;
    background: rgba(255,255,255,0.025);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 18px;
  }
  .contact-social-label { font-family: 'IBM Plex Mono', monospace; font-size: 0.625rem; letter-spacing: 0.14em; text-transform: uppercase; color: rgba(255,255,255,0.35); margin-bottom: 0.875rem; }
  .contact-social-links { display: flex; flex-wrap: wrap; gap: 0.6rem; }
  .contact-social-link {
    display: inline-flex; align-items: center; gap: 0.5rem;
    padding: 0.5rem 1rem; border-radius: 10px;
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.65); font-size: 0.8125rem; text-decoration: none;
    transition: all 0.25s cubic-bezier(0.34,1.56,0.64,1);
  }
  .contact-social-link:hover { background: rgba(184,255,61,0.1); border-color: rgba(184,255,61,0.25); color: white; transform: translateY(-2px); }

  /* Form card */
  .contact-v2-form-wrap {
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.09);
    border-radius: 28px; padding: 2.75rem;
    backdrop-filter: blur(16px);
    box-shadow: 0 32px 80px rgba(0,0,0,0.3);
    position: relative; overflow: hidden;
  }
  .contact-v2-form-wrap::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(184,255,61,0.45), transparent);
  }
  .contact-v2-form-title { font-family: 'Space Grotesk', sans-serif; font-size: 1.5rem; font-weight: 700; color: white; margin-bottom: 0.35rem; letter-spacing: -0.02em; }
  .contact-v2-form-subtitle { color: rgba(255,255,255,0.45); font-size: 0.9rem; margin-bottom: 1.75rem; line-height: 1.6; }

  .contact-v2-tabs { display: flex; gap: 0.5rem; margin-bottom: 1.75rem; background: rgba(255,255,255,0.04); border-radius: 14px; padding: 0.3rem; }
  .contact-v2-tab {
    flex: 1; padding: 0.6rem 1rem; border-radius: 10px; font-size: 0.8125rem; font-weight: 500;
    border: none; background: transparent; color: rgba(255,255,255,0.5);
    cursor: pointer; transition: all 0.3s cubic-bezier(0.4,0,0.2,1); font-family: 'Inter', sans-serif;
  }
  .contact-v2-tab.active { background: var(--lime); color: var(--navy); font-weight: 700; box-shadow: 0 4px 14px rgba(184,255,61,0.3); }
  .contact-v2-tab:hover:not(.active) { background: rgba(255,255,255,0.07); color: white; }

  .contact-v2-field { margin-bottom: 1.2rem; position: relative; }
  .contact-v2-field label { display: block; font-size: 0.78rem; font-weight: 500; margin-bottom: 0.4rem; color: rgba(255,255,255,0.6); letter-spacing: 0.02em; transition: color 0.2s; }
  .contact-v2-field:focus-within label { color: var(--lime); }
  .contact-v2-field input, .contact-v2-field textarea {
    width: 100%; padding: 0.875rem 1.1rem;
    background: rgba(255,255,255,0.05);
    border: 1.5px solid rgba(255,255,255,0.1);
    border-radius: 14px; font-size: 0.9375rem; outline: none;
    transition: all 0.3s cubic-bezier(0.4,0,0.2,1);
    font-family: 'Inter', sans-serif; color: white;
  }
  .contact-v2-field input::placeholder, .contact-v2-field textarea::placeholder { color: rgba(255,255,255,0.28); }
  .contact-v2-field input:focus, .contact-v2-field textarea:focus {
    border-color: var(--lime); background: rgba(184,255,61,0.05);
    box-shadow: 0 0 0 4px rgba(184,255,61,0.1); transform: translateY(-1px);
  }
  .contact-v2-field textarea { resize: none; min-height: 120px; }
  .contact-v2-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }

  .contact-v2-chip-group { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.5rem; }
  .contact-v2-chip {
    padding: 0.42rem 1rem; border-radius: 9999px; font-size: 0.78rem; font-weight: 500;
    border: 1.5px solid rgba(255,255,255,0.1);
    background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.55);
    cursor: pointer; transition: all 0.28s cubic-bezier(0.34,1.56,0.64,1); font-family: 'Inter', sans-serif;
  }
  .contact-v2-chip:hover { border-color: rgba(184,255,61,0.35); color: rgba(255,255,255,0.9); transform: translateY(-2px); }
  .contact-v2-chip.selected {
    background: rgba(184,255,61,0.14); border-color: var(--lime);
    color: var(--lime); font-weight: 600;
    box-shadow: 0 4px 14px rgba(184,255,61,0.18);
  }

  .contact-v2-submit {
    width: 100%; padding: 1.1rem; margin-top: 0.75rem;
    background: var(--lime); color: var(--navy);
    border: none; border-radius: 14px; font-size: 1rem; font-weight: 700;
    cursor: pointer; font-family: 'Inter', sans-serif;
    display: flex; align-items: center; justify-content: center; gap: 0.6rem;
    transition: all 0.35s cubic-bezier(0.34,1.56,0.64,1);
    position: relative; overflow: hidden;
  }
  .contact-v2-submit::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(135deg, rgba(255,255,255,0.18), transparent);
    opacity: 0; transition: opacity 0.3s;
  }
  .contact-v2-submit:hover { transform: translateY(-3px) scale(1.02); box-shadow: 0 14px 38px rgba(184,255,61,0.45); }
  .contact-v2-submit:hover::after { opacity: 1; }
  .contact-v2-submit:active { transform: translateY(0) scale(0.98); }

  .contact-v2-success {
    text-align: center; padding: 3rem 1rem;
    display: flex; flex-direction: column; align-items: center; gap: 1.25rem;
    animation: fadeInUp 0.6s cubic-bezier(0.22,1,0.36,1);
  }
  .contact-v2-success-icon {
    width: 84px; height: 84px;
    background: rgba(184,255,61,0.1); border: 2px solid rgba(184,255,61,0.28);
    border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 2.6rem;
    animation: successPop 0.55s cubic-bezier(0.34,1.56,0.64,1) 0.15s both;
  }
  .contact-v2-success h3 { font-size: 1.75rem; font-weight: 700; color: white; }
  .contact-v2-success p { color: rgba(255,255,255,0.55); line-height: 1.7; max-width: 300px; }

  @media (max-width: 968px) {
    .contact-v2-grid { grid-template-columns: 1fr; }
    .contact-v2-field-row { grid-template-columns: 1fr; }
  }

  /* ── FEEDBACK SECTION ── */
  .feedback-section {
    padding: 6rem 2rem; background: linear-gradient(135deg, #0B0F17 0%, #172136 100%);
    position: relative; overflow: hidden;
    border-top: 1px solid rgba(255,255,255,0.04);
  }
  .feedback-section::before {
    content: ''; position: absolute; top: -200px; right: -200px;
    width: 500px; height: 500px; border-radius: 50%;
    background: radial-gradient(circle, rgba(184,255,61,0.08) 0%, transparent 70%);
    pointer-events: none;
  }
  .feedback-inner { max-width: 1200px; margin: 0 auto; position: relative; }
  .feedback-header { text-align: center; margin-bottom: 3rem; }
  .feedback-header h2 { font-size: clamp(2rem,4vw,2.75rem); font-weight: 600; color: white; margin-bottom: 1rem; }
  .feedback-header p { color: rgba(255,255,255,0.6); max-width: 480px; margin: 0 auto; line-height: 1.7; }

  .feedback-grid { display: flex; justify-content: center; width: 100%; }
  .feedback-form-wrap {
    width: 100%; max-width: 600px;
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
    border-radius: 24px; padding: 2.5rem;
  }
  .feedback-form-wrap h3 { color: white; font-size: 1.25rem; font-weight: 600; margin-bottom: 1.5rem; }
  .feedback-field { margin-bottom: 1.25rem; }
  .feedback-field label { display: block; font-size: 0.8125rem; font-weight: 500; margin-bottom: 0.375rem; color: rgba(255,255,255,0.7); }
  .feedback-field input, .feedback-field textarea, .feedback-field select {
    width: 100%; padding: 0.875rem 1rem;
    background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
    border-radius: 12px; font-size: 0.875rem; outline: none; transition: all 0.2s;
    font-family: 'Inter', sans-serif; color: white;
  }
  .feedback-field input::placeholder, .feedback-field textarea::placeholder { color: rgba(255,255,255,0.35); }
  .feedback-field input:focus, .feedback-field textarea:focus, .feedback-field select:focus { border-color: rgba(184,255,61,0.5); background: rgba(255,255,255,0.08); }
  .feedback-field textarea { resize: none; min-height: 110px; }
  .feedback-field select option { background: var(--navy); }

  .rating-group { display: flex; gap: 0.75rem; flex-wrap: wrap; }
  .rating-btn {
    width: 44px; height: 44px; border-radius: 50%;
    border: 1.5px solid rgba(255,255,255,0.15); background: rgba(255,255,255,0.05);
    color: rgba(255,255,255,0.6); font-size: 1.25rem; cursor: pointer;
    transition: all 0.2s; display: flex; align-items: center; justify-content: center;
    font-family: 'Inter', sans-serif;
  }
  .rating-btn:hover, .rating-btn.selected { background: rgba(184,255,61,0.15); border-color: var(--lime); transform: scale(1.1); }

  .feedback-submit {
    width: 100%; padding: 1rem; background: var(--lime); color: var(--navy);
    border: none; border-radius: 12px; font-size: 0.9375rem; font-weight: 600;
    cursor: pointer; transition: all 0.2s; font-family: 'Inter', sans-serif;
    margin-top: 0.5rem;
  }
  .feedback-submit:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(184,255,61,0.3); }

  .feedback-testimonials { display: flex; flex-direction: column; gap: 1.25rem; }
  .testimonial-card {
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
    border-radius: 20px; padding: 1.5rem;
  }
  .testimonial-stars { color: var(--lime); font-size: 0.875rem; margin-bottom: 0.75rem; }
  .testimonial-text { color: rgba(255,255,255,0.8); font-size: 0.9375rem; line-height: 1.6; margin-bottom: 1rem; font-style: italic; }
  .testimonial-author { display: flex; align-items: center; gap: 0.75rem; }
  .testimonial-avatar {
    width: 36px; height: 36px; border-radius: 50%;
    background: rgba(184,255,61,0.2); display: flex; align-items: center; justify-content: center;
    font-weight: 600; color: var(--lime); font-size: 0.875rem;
  }
  .testimonial-name { font-size: 0.875rem; font-weight: 600; color: white; }
  .testimonial-role { font-size: 0.75rem; color: rgba(255,255,255,0.4); }

  .success-toast {
    position: fixed; bottom: 2rem; right: 2rem; z-index: 3000;
    background: var(--navy); color: white; border: 1px solid rgba(184,255,61,0.3);
    border-radius: 16px; padding: 1rem 1.5rem;
    display: flex; align-items: center; gap: 0.75rem;
    box-shadow: 0 20px 60px rgba(0,0,0,0.4);
    animation: slideInToast 0.4s cubic-bezier(0.22,1,0.36,1);
  }
  @keyframes slideInToast { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
  .toast-icon { font-size: 1.25rem; }
  .toast-msg { font-size: 0.875rem; font-weight: 500; }

  @media (max-width: 968px) {
    .nav-links { display: none; }
    .feature-split { grid-template-columns: 1fr; }
    .feature-image { height: 300px; order: -1; }
    .step-card, .value-card { margin-left: 0 !important; grid-template-columns: 1fr; }
    .step-image { width: 100%; height: 200px; }
    .partner-grid { grid-template-columns: 1fr; }
    .footer-grid { grid-template-columns: 1fr; gap: 2rem; }
    .demo-modal { grid-template-columns: 1fr; }
    .demo-left { display: none; }
    .contact-grid { grid-template-columns: 1fr; gap: 2rem; }
    .contact-stat-strip { grid-template-columns: repeat(3,1fr); }

    .demo-stat-grid { grid-template-columns: repeat(2,1fr); }
    .contact-field-row { grid-template-columns: 1fr; }
  }
  /* ── INTERACTIVE DEMO V2 STYLES ── */
  .upload-dropzone {
    border: 2px dashed rgba(184,255,61,0.3);
    border-radius: 20px;
    padding: 4rem 2rem;
    text-align: center;
    background: rgba(184,255,61,0.03);
    transition: all 0.3s ease;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
  .upload-dropzone:hover {
    border-color: var(--lime);
    background: rgba(184,255,61,0.08);
    transform: translateY(-2px);
  }
  .upload-icon {
    font-size: 3rem;
    margin-bottom: 0.5rem;
  }
  .upload-text {
    color: white;
    font-size: 1.1rem;
    font-weight: 600;
  }
  .upload-subtext {
    color: rgba(255,255,255,0.5);
    font-size: 0.9rem;
    max-width: 400px;
  }
  
  .calculating-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
  }
  .spinner {
    width: 60px;
    height: 60px;
    border: 4px solid rgba(184,255,61,0.1);
    border-top-color: var(--lime);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 2rem;
  }
  @keyframes spin { 100% { transform: rotate(360deg); } }
  
  .dashboard-results {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
    animation: fadeInUp 0.5s ease;
  }
  
  .pdf-preview {
    background: white;
    border-radius: 12px;
    padding: 2rem;
    color: #1a1a1a;
    box-shadow: 0 20px 40px rgba(0,0,0,0.3);
    position: relative;
    height: 100%;
    min-height: 400px;
    display: flex;
    flex-direction: column;
  }
  .pdf-header {
    border-bottom: 2px solid #e5e7eb;
    padding-bottom: 1rem;
    margin-bottom: 1.5rem;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }
  .pdf-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #111827;
  }
  .pdf-meta {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.25rem;
  }
  .pdf-content-skeleton {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  .pdf-line {
    height: 12px;
    background: #f3f4f6;
    border-radius: 4px;
    width: 100%;
  }
  .pdf-line.short { width: 60%; }
  .pdf-line.medium { width: 85%; }
  .pdf-box {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 1rem;
    margin: 1rem 0;
  }
  .download-btn {
    position: absolute;
    bottom: -1rem;
    right: -1rem;
    background: var(--navy);
    color: white;
    border: 1px solid rgba(255,255,255,0.2);
    padding: 0.75rem 1.5rem;
    border-radius: 99px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 10px 25px rgba(0,0,0,0.2);
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .download-btn:hover {
    background: var(--lime);
    color: var(--navy);
    transform: translateY(-2px);
  }
  
  .scope-card {
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 16px;
    padding: 1.5rem;
    margin-bottom: 1rem;
    transition: all 0.3s ease;
  }
  .scope-card:hover {
    background: rgba(255,255,255,0.05);
    border-color: rgba(184,255,61,0.2);
    transform: translateX(4px);
  }
  .scope-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }
  .scope-label {
    font-weight: 600;
    color: white;
    font-size: 1.1rem;
  }
  .scope-value {
    color: var(--lime);
    font-family: 'IBM Plex Mono', monospace;
    font-weight: 700;
    font-size: 1.25rem;
  }
  .scope-progress-bg {
    height: 8px;
    background: rgba(255,255,255,0.1);
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 0.5rem;
  }
  .scope-progress-fill {
    height: 100%;
    background: var(--lime);
    border-radius: 4px;
    transition: width 1s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .scope-desc {
    font-size: 0.75rem;
    color: rgba(255,255,255,0.5);
  }
  
  .insight-card {
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 20px;
    padding: 2rem;
    display: flex;
    gap: 1.5rem;
    transition: all 0.3s ease;
    cursor: default;
    margin-bottom: 1.5rem;
    /* Entry Animation */
    opacity: 0;
    animation: insightsFadeSlide 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  }
  @keyframes insightsFadeSlide {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .insight-card:hover {
    border-color: var(--lime);
    background: rgba(184,255,61,0.05);
    transform: translateY(-4px);
  }
  .insight-icon {
    width: 60px;
    height: 60px;
    background: rgba(184,255,61,0.1);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    flex-shrink: 0;
  }
  .insight-content h4 {
    color: white;
    font-size: 1.25rem;
    margin-bottom: 0.5rem;
  }
  .insight-content p {
    color: rgba(255,255,255,0.6);
    font-size: 0.9rem;
    line-height: 1.6;
    margin-bottom: 1rem;
  }
  .insight-footer {
    display: flex;
    gap: 1rem;
    align-items: center;
  }
  .insight-impact {
    background: rgba(184,255,61,0.15);
    color: var(--lime);
    padding: 0.25rem 0.75rem;
    border-radius: 99px;
    font-size: 0.75rem;
    font-weight: 600;
  }
  .insight-difficulty {
    color: rgba(255,255,255,0.4);
    font-size: 0.75rem;
  }
  
  .empty-state {
    text-align: center;
    padding: 4rem 2rem;
    background: rgba(255,255,255,0.02);
    border-radius: 20px;
    border: 1px dashed rgba(255,255,255,0.1);
  }
  
  @media (max-width: 768px) {
    .dashboard-results { grid-template-columns: 1fr; }
    .insight-card { flex-direction: column; }
  }
`;

const DEMO_TABS = ["Dashboard", "Analytics", "Insights"];

const demoData = {
  Dashboard: {
    calculatedStats: [
      { label: "Scope 1 (Direct)", value: "112 tCO₂", scope: "Fleet & Facilities", percent: 34 },
      { label: "Scope 2 (Indirect)", value: "86 tCO₂", scope: "Purchased Electricity", percent: 27 },
      { label: "Scope 3 (Value Chain)", value: "126 tCO₂", scope: "Business Travel & Supply", percent: 39 },
    ]
  },
  Analytics: {
    historyBars: [
      { s1: 15, s2: 12, s3: 15 },
      { s1: 14, s2: 11, s3: 13 },
      { s1: 18, s2: 15, s3: 22 },
      { s1: 16, s2: 14, s3: 18 },
      { s1: 20, s2: 18, s3: 22 },
      { s1: 17, s2: 15, s3: 18 },
      { s1: 22, s2: 20, s3: 23 },
      { s1: 20, s2: 18, s3: 20 },
      { s1: 25, s2: 20, s3: 25 },
      { s1: 21, s2: 18, s3: 23 },
      { s1: 26, s2: 21, s3: 28 },
      { s1: 28, s2: 22, s3: 30 }
    ],
    historyLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    scopeBreakdown: [
      { name: "Scope 1 (Direct)", percent: 34, color: "var(--lime)" },
      { name: "Scope 2 (Indirect)", percent: 27, color: "#2E5BFF" },
      { name: "Scope 3 (Value Chain)", percent: 39, color: "#FFB020" }
    ],
    topSources: [
      { name: "Business Travel (Flights)", value: "54 tCO₂" },
      { name: "Grid Electricity (HQ)", value: "38 tCO₂" },
      { name: "Logistics Delivery Fleet", value: "32 tCO₂" },
      { name: "Supply Chain Packaging", value: "18 tCO₂" }
    ]
  },
  Insights: {
    strategies: [
      { 
        title: "Switch to Renewable Energy Provider", 
        description: "Your recent electricity invoice shows high Scope 2 emissions. Switching to a green tariff could reduce your overall footprint substantially.",
        impact: "-45 tCO₂/yr",
        difficulty: "Easy",
        icon: "⚡"
      },
      { 
        title: "Electrify Logistics Fleet", 
        description: "Fuel logs indicate diesel usage is the primary driver of Scope 1. Transitioning to EV delivery vans for urban routes offers immediate reduction.",
        impact: "-80 tCO₂/yr",
        difficulty: "Medium",
        icon: "🚚"
      },
      { 
        title: "Optimize Business Travel", 
        description: "Flight records contribute heavily to Scope 3. Implementing a 'virtual-first' policy for internal meetings could mitigate these emissions.",
        impact: "-20 tCO₂/yr",
        difficulty: "Easy",
        icon: "✈️"
      }
    ]
  }
};

export default function CarbonSync() {
  const navRef = useRef(null);

  // Demo landing
  const [showDemoModal, setShowDemoModal] = useState(true);
  const [demoStep, setDemoStep] = useState(1);
  const [demoForm, setDemoForm] = useState({ firstName: "", lastName: "", email: "", company: "", role: "", size: "" });

  // Demo interactive section
  const [activeTab, setActiveTab] = useState("Dashboard");
  const [demoInvoiceUploaded, setDemoInvoiceUploaded] = useState(false);
  const [demoUploading, setDemoUploading] = useState(false);

  // Contact form
  const [contactForm, setContactForm] = useState({ name: "", email: "", company: "", type: "", message: "", phone: "" });
  const [contactSent, setContactSent] = useState(false);
  const [contactTab, setContactTab] = useState("Message");
  const [contactChips, setContactChips] = useState([]);
  const toggleChip = (chip) => setContactChips(prev => prev.includes(chip) ? prev.filter(c => c !== chip) : [...prev, chip]);

  // Feedback form
  const [feedbackForm, setFeedbackForm] = useState({ email: "", category: "", rating: "", message: "" });
  const [feedbackSent, setFeedbackSent] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);

  // Countdown timer
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, mins: 0, secs: 0 });
  const [notifyClicked, setNotifyClicked] = useState(false);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    const target = new Date("2026-05-28T00:00:00");
    const tick = () => {
      const now = new Date();
      const diff = Math.max(0, target - now);
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        mins: Math.floor((diff % 3600000) / 60000),
        secs: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const styleTag = document.createElement("style");
    styleTag.innerHTML = styles;
    document.head.appendChild(styleTag);

    const handleScroll = () => {
      if (navRef.current) {
        navRef.current.classList.toggle("scrolled", window.scrollY > 100);
      }
    };
    window.addEventListener("scroll", handleScroll);

    const observerOptions = { threshold: 0.1, rootMargin: "0px 0px -50px 0px" };
    const glassObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = "1";
          entry.target.style.transform = "translateY(0)";
        }
      });
    }, observerOptions);

    document.querySelectorAll(".glass-card").forEach((el) => {
      el.style.opacity = "0";
      el.style.transform = "translateY(30px)";
      el.style.transition = "opacity 0.6s ease, transform 0.6s ease";
      glassObserver.observe(el);
    });

    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add("visible");
      });
    }, { threshold: 0.1, rootMargin: "0px 0px -40px 0px" });

    document.querySelectorAll(".reveal, .reveal-left, .reveal-right, .reveal-scale").forEach((el) => {
      revealObserver.observe(el);
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      glassObserver.disconnect();
      revealObserver.disconnect();
      document.head.removeChild(styleTag);
    };
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleDemoUpload = () => {
    setDemoUploading(true);
    setTimeout(() => {
      setDemoUploading(false);
      setDemoInvoiceUploaded(true);
      showToast("Invoice processed! Emissions calculated.");
    }, 2500);
  };

  const handleDemoNext = async (e) => {
    e.preventDefault();
    if (demoStep === 1) { setDemoStep(2); return; }

    // Post to Formspree (replace your_form_id with actual Formspree ID)
    await fetch("https://formspree.io/f/xwvwblzg", {
      method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({ formType: "Demo Request", ...demoForm })
    }).catch(console.error);

    setShowDemoModal(false);
    showToast("🎉 Demo booked! Check your email for the invite.");
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch("https://formspree.io/f/xojyggok", {
     
        method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ formType: "Contact Message", ...contactForm, topic: contactTab, selectedChips: contactChips })
      });
      if (response.ok) {
        setContactSent(true);
        showToast("✅ Message sent! We'll reply within 24 hours.");
        setContactForm({ name: "", email: "", company: "", type: "", message: "", phone: "" });
        setContactChips([]);
      } else {
        showToast("✅ Message sent! We'll reply within 24 hours.");
      }
    } catch (error) { showToast("✅ Message sent! We'll reply within 24 hours.");; }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch("https://formspree.io/f/xojyggok", {
        method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ formType: "Feedback", ...feedbackForm })
      });
      if (response.ok) {
        setFeedbackSent(true);
        showToast("💚 Thank you for your feedback!");
        setFeedbackForm({ email: "", category: "", rating: "", message: "" });
      } else { showToast("❌ Error submitting feedback."); }
    } catch (e) { showToast("❌ Error submitting feedback."); }
  };

  const data = demoData[activeTab];

  return (
    <>
      <div className="noise" />

      {/* ── DEMO LANDING MODAL ── */}
      {showDemoModal && (
        <div className="demo-overlay">
          <div className="demo-modal" style={{ position: "relative" }}>
            <div className="demo-left">
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '2rem' }}>
                  <img src={process.env.PUBLIC_URL + "/Logo2.png"} alt="Logo" style={{ width: '40px', height: 'auto' }} />
                  <div className="demo-logo" style={{ marginBottom: 0 }}>Carbon<span>Sync</span></div>
                </div>
                <h2>See CarbonSync in Action</h2>
                <p>Get a personalized walkthrough of how your business data turns into emissions, reports, and reduction insights.</p>
                <ul className="demo-features">
                  {["Real Data to Emissions Demo", "Scope 1/2/3 accounting walkthrough", "Custom compliance report preview", "Audit-Ready Report Preview"].map(f => (
                    <li key={f}><div className="demo-check">✓</div>{f}</li>
                  ))}
                </ul>
              </div>
              <div style={{ marginTop: "2rem", padding: "1rem", background: "rgba(184,255,61,0.08)", borderRadius: "12px", border: "1px solid rgba(184,255,61,0.2)" }}>
                <div style={{ color: "var(--lime)", fontFamily: "'IBM Plex Mono',monospace", fontSize: "0.625rem", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "0.5rem" }}>30-minute session</div>
                <div style={{ color: "white", fontSize: "0.875rem" }}>No commitment required. Works across all time zones.</div>
              </div>
            </div>
            <div className="demo-right">
              <button className="demo-close" onClick={() => setShowDemoModal(false)}>✕</button>
              <div className="demo-steps">
                {[1, 2].map(s => (
                  <div key={s} className={`demo-step-dot${demoStep >= s ? " active" : ""}`} />
                ))}
              </div>
              {demoStep === 1 ? (
                <>
                  <h3>Request your demo</h3>
                  <p style={{ color: "var(--text-secondary)", fontSize: "0.875rem", marginBottom: "1.5rem" }}>Tell us a bit about you so we can personalise your session.</p>
                  <div className="demo-form-row">
                    <div className="demo-form-group">
                      <label>First name</label>
                      <input placeholder="Priya" value={demoForm.firstName} onChange={e => setDemoForm({ ...demoForm, firstName: e.target.value })} required />
                    </div>
                    <div className="demo-form-group">
                      <label>Last name</label>
                      <input placeholder="Sharma" value={demoForm.lastName} onChange={e => setDemoForm({ ...demoForm, lastName: e.target.value })} required />
                    </div>
                  </div>
                  <div className="demo-form-group">
                    <label>Work email</label>
                    <input type="email" placeholder="priya@company.com" value={demoForm.email} onChange={e => setDemoForm({ ...demoForm, email: e.target.value })} required />
                  </div>
                  <div className="demo-form-group">
                    <label>Company</label>
                    <input placeholder="Your company" value={demoForm.company} onChange={e => setDemoForm({ ...demoForm, company: e.target.value })} required />
                  </div>
                  <button className="demo-submit" onClick={handleDemoNext}>Continue →</button>
                </>
              ) : (
                <>
                  <h3>Almost there!</h3>
                  <p style={{ color: "var(--text-secondary)", fontSize: "0.875rem", marginBottom: "1.5rem" }}>Help us tailor the demo to your use case.</p>
                  <div className="demo-form-group">
                    <label>Your role</label>
                    <select value={demoForm.role} onChange={e => setDemoForm({ ...demoForm, role: e.target.value })}>
                      <option value="">Select your role</option>
                      <option>Sustainability Lead</option>
                      <option>CFO / Finance</option>
                      <option>Compliance Officer</option>
                      <option>Project Developer</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div className="demo-form-group">
                    <label>Company size</label>
                    <select value={demoForm.size} onChange={e => setDemoForm({ ...demoForm, size: e.target.value })}>
                      <option value="">Select size</option>
                      <option>1–50 employees</option>
                      <option>51–500 employees</option>
                      <option>500–5,000 employees</option>
                      <option>5,000+ employees</option>
                    </select>
                  </div>
                  <div className="demo-form-group">
                    <label>Primary interest</label>
                    <select>
                      <option value="">Select interest</option>
                      <option>Carbon accounting</option>
                      <option>Credit marketplace</option>
                      <option>Compliance reporting</option>
                      <option>All of the above</option>
                    </select>
                  </div>
                  <button className="demo-submit" onClick={handleDemoNext}>Book my demo 🎉</button>
                </>
              )}
              <div className="demo-skip">
                <button onClick={() => setShowDemoModal(false)}>Skip for now</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav id="navbar" ref={navRef}>
        <a href="#" className="logo" style={{ display: 'flex', alignItems: 'center', gap: '8px' }} onClick={(e) => handleNavClick(e, "#hero")}>
          <img src={process.env.PUBLIC_URL + "/Logo2.png"} alt="Logo" style={{ width: '50px', height: 'auto' }} />
          CarbonSync
        </a>
        <ul className="nav-links">
          <li><a href="#solutions" onClick={(e) => handleNavClick(e, "#solutions")}>Solutions</a></li>
          <li><a href="#marketplace" onClick={(e) => handleNavClick(e, "#marketplace")}>Marketplace</a></li>
          <li><a href="#demo" onClick={(e) => handleNavClick(e, "#demo")}>Live Demo</a></li>
          <li><a href="#contact-us" onClick={(e) => handleNavClick(e, "#contact-us")}>Contact</a></li>
          <li><a href="#feedback" onClick={(e) => handleNavClick(e, "#feedback")}>Feedback</a></li>
        </ul>
        <button className="btn-primary" onClick={() => setShowDemoModal(true)}>Request demo</button>
      </nav>

      {/* Hero Section */}
      <section className="section" id="hero">
        <div className="section-bg">
          <img src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1920&q=80" alt="Aerial landscape" />
        </div>
        <div className="section-content">
          <div className="glass-card hero-card animate-in">
            <span className="title-pill">
              <span style={{ width: 6, height: 6, background: "var(--lime)", borderRadius: "50%", display: "inline-block" }} />
              ESG-Ready Carbon Reports in Minutes
            </span>
            <h1>Carbon Accounting & Net Zero—Simplified</h1>
            <p>Upload your invoices and business data. Get accurate Scope 1, 2 & 3 emissions reports—and AI-driven strategies to reduce your footprint.</p>
            <div className="hero-buttons">
              <button className="btn-primary" onClick={() => setShowDemoModal(true)}>Explore Platform →</button>
              <a href="#demo" className="btn-secondary" onClick={(e) => handleNavClick(e, "#demo")}>📊 Try live demo</a>
            </div>
          </div>
        </div>
      </section>

      {/* ── LAUNCH BANNER SECTION ── */}
      <section className="launch-section" id="launch">
        {/* Confetti pieces */}
        {[
          { color: "#B8FF3D", left: "8%", delay: "0.0s", dur: "3.2s", size: 10, shape: "rect" },
          { color: "#f093fb", left: "15%", delay: "0.6s", dur: "4.1s", size: 7, shape: "circle" },
          { color: "#fff", left: "22%", delay: "1.2s", dur: "3.7s", size: 9, shape: "rect" },
          { color: "#FEBC2E", left: "35%", delay: "0.3s", dur: "5.0s", size: 6, shape: "circle" },
          { color: "#B8FF3D", left: "48%", delay: "0.9s", dur: "3.4s", size: 8, shape: "rect" },
          { color: "#667eea", left: "58%", delay: "0.1s", dur: "4.5s", size: 10, shape: "circle" },
          { color: "#f093fb", left: "67%", delay: "0.7s", dur: "3.9s", size: 7, shape: "rect" },
          { color: "#fff", left: "76%", delay: "1.4s", dur: "4.2s", size: 6, shape: "circle" },
          { color: "#FEBC2E", left: "85%", delay: "0.4s", dur: "3.6s", size: 9, shape: "rect" },
          { color: "#B8FF3D", left: "93%", delay: "1.0s", dur: "5.1s", size: 7, shape: "circle" },
        ].map((c, i) => (
          <div key={i} className="confetti-piece" style={{
            left: c.left,
            top: "-20px",
            background: c.color,
            width: c.size,
            height: c.size,
            borderRadius: c.shape === "circle" ? "50%" : "2px",
            animationDelay: c.delay,
            animationDuration: c.dur,
          }} />
        ))}

        <div className="launch-inner">
          {/* Left card: What's coming */}
          <div className="launch-card reveal reveal-left td-0">
            <div className="launch-icon-wrap">🌱</div>
            <div className="launch-side-card-title">What You Can Do With CarbonSync</div>
            <div className="launch-side-card-text">Carbon accounting and net zero platform built for India’s compliance needs and global reporting standards.</div>
            <ul className="launch-feat-list">
              <li>Automated Carbon Accounting</li>
              <li>Standardized ESG Reports</li>
              <li>AI-Powered Reduction Insights</li>
              <li>Centralized Emissions Dashboard</li>
            </ul>
          </div>

          {/* Centre main card: Launching soon */}
          <div className="launch-card-main reveal-scale td-2">
            <div className="launch-badge">
              <span style={{ width: 6, height: 6, background: "#B8FF3D", borderRadius: "50%", display: "inline-block", boxShadow: "0 0 6px #B8FF3D", animation: "pulse 1.5s ease-in-out infinite" }} />
              Coming Soon
            </div>
            <div className="launch-title">We're launching soon! 🎉</div>
            <div className="launch-subtitle">Stay tuned for something that Gets audit-ready carbon reports and actionable reduction strategies. 🔔</div>
            <div className="launch-countdown">
              {[
                { num: String(timeLeft.days).padStart(2, "0"), label: "Days" },
                { num: String(timeLeft.hours).padStart(2, "0"), label: "Hours" },
                { num: String(timeLeft.mins).padStart(2, "0"), label: "Mins" },
                { num: String(timeLeft.secs).padStart(2, "0"), label: "Secs" },
              ].map(({ num, label }) => (
                <div className="countdown-block" key={label}>
                  <div className="countdown-num">{num}</div>
                  <div className="countdown-label">{label}</div>
                </div>
              ))}
            </div>
            <button
              className="launch-notify-btn"
              onClick={() => { setNotifyClicked(true); showToast("🔔 You'll be the first to know when we launch!"); }}
            >
              {notifyClicked ? "✓ You're on the list!" : "Notify me at launch →"}
            </button>
          </div>

          {/* Right card: Why it matters */}
          <div className="launch-card reveal reveal-right td-4">
            <div className="launch-icon-wrap">⚡</div>
            <div className="launch-side-card-title">Why it matters</div>
            <div className="launch-side-card-text">Carbon reporting is becoming mandatory—and spreadsheets won’t keep up.</div>
            <ul className="launch-feat-list">
              <li>Stay Compliant Without the Chaos</li>
              <li>Turn Raw Data Into Real Insights</li>
              <li>Reduce Costs, Not Just Emissions</li>
              <li>Be Ready for ESG and Audits</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Corporate Accounting Section */}
      <section className="section" id="solutions">
        <div className="section-bg">
          <img src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80" alt="Modern building" />
        </div>
        <div className="section-content">
          <div className="feature-split">
            <div className="glass-card feature-card">
              <span className="micro-label">Corporate Accounting</span>
              <h2>Measure. Report. Reduce.</h2>
              <p>Turn your invoices, fuel usage, and energy data into accurate Scope 1, 2 & 3 emissions—complete with audit-ready reports and actionable reduction insights.</p>
              <a href="#" className="btn-secondary">📊 See accounting features →</a>
            </div>
            <div className="feature-image">
              <img src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80" alt="Corporate office" />
            </div>
          </div>
        </div>
      </section>

      {/* Marketplace Section */}
      <section className="section" id="marketplace">
        <div className="section-bg">
          <img src="https://images.unsplash.com/photo-1439066615861-d1af74d74000?w=1920&q=80" alt="Water aerial" />
        </div>
        <div className="section-content">
          <div className="glass-card hero-card">
            <span className="title-pill">
              <span style={{ width: 6, height: 6, background: "var(--lime)", borderRadius: "50%", display: "inline-block" }} />
              Carbon Offsets & Credits
            </span>
            <h1>Offset what you can’t reduce.</h1>
            <p>Purchase and retire verified carbon credits directly from your emissions dashboard—with transparent pricing and clear documentation for compliance.</p>
            <div className="hero-buttons">
              <a href="#demo" className="btn-primary" onClick={(e) => handleNavClick(e, "#demo")}>🛒 Explore Offsets →</a>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="section" id="compliance">
        <div className="section-bg">
          <img src="https://images.unsplash.com/photo-1577495508048-b635879837f1?w=1920&q=80" alt="Building facade" />
        </div>
        <div className="section-content">
          <div className="feature-split">
            <div className="glass-card feature-card">
              <span className="micro-label">Compliance & Reporting</span>
              <h2>Audit-ready from day one.</h2>
              <p>Generate standardized Scope 1, 2 & 3 reports with clear, traceable data—aligned with India’s regulatory frameworks and global ESG standards.</p>
              <a href="#" className="btn-secondary">🛡️ Explore compliance →</a>
            </div>
            <div className="feature-image">
              <img src="https://images.unsplash.com/photo-1577495508048-b635879837f1?w=800&q=80" alt="Compliance office" />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="flowing-section" id="how-it-works">
        <div className="section-header reveal">
          <h2>How CarbonSync works</h2>
          <p>From raw business data to measurable emissions and reduction insights.</p>
        </div>
        <div className="steps">
          <div className="glass-card step-card">
            <div>
              <div className="step-icon">📤</div>
              <div className="step-number">01</div>
              <h3>Upload Your Data</h3>
              <p>Upload invoices, fuel records, and electricity bills—or connect your systems directly.</p>
            </div>
            <div className="step-image">
              <img src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=400&q=80" alt="Data connection" />
            </div>
          </div>
          <div className="glass-card step-card">
            <div>
              <div className="step-icon">✓</div>
              <div className="step-number">02</div>
              <h3>Calculate Emissions</h3>
              <p>We convert your data into accurate Scope 1, 2 & 3 emissions using standardized factors.</p>
            </div>
            <div className="step-image">
              <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&q=80" alt="Verification" />
            </div>
          </div>
          <div className="glass-card step-card">
            <div>
              <div className="step-icon">🔒</div>
              <div className="step-number">03</div>
              <h3>Get Reports & Insights</h3>
              <p>Access audit-ready reports and AI-driven recommendations to reduce your carbon footprint.</p>
            </div>
            <div className="step-image">
              <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=400&q=80" alt="Settlement" />
            </div>
          </div>
        </div>
      </section>

      {/* ── INTERACTIVE DEMO SECTION ── */}
      <section className="demo-section" id="demo" style={{ paddingBottom: "8rem" }}>
        <div className="demo-section-inner">
          <div className="demo-section-header">
            <span className="title-pill-dark" style={{ marginBottom: "1rem", display: "inline-flex" }}>
              <span style={{ width: 6, height: 6, background: "var(--lime)", borderRadius: "50%", display: "inline-block" }} />
              Live Platform Demo
            </span>
            <h2>See CarbonSync in action</h2>
            <p>Explore the actual platform interface — no login required.</p>
          </div>
          <div className="demo-tabs">
            {DEMO_TABS.map(tab => (
              <button key={tab} className={`demo-tab${activeTab === tab ? " active" : ""}`} onClick={() => setActiveTab(tab)}>{tab}</button>
            ))}
          </div>
          <div className="demo-screen">
            <div className="demo-screen-header">
              <div className="demo-dot" style={{ background: "#FF5F57" }} />
              <div className="demo-dot" style={{ background: "#FEBC2E" }} />
              <div className="demo-dot" style={{ background: "#28C840" }} />
              <span style={{ color: "rgba(255,255,255,0.3)", fontFamily: "'IBM Plex Mono',monospace", fontSize: "0.75rem", marginLeft: "0.5rem" }}>carbonsync.io / {activeTab.toLowerCase()}</span>
            </div>

            {activeTab === "Dashboard" && (
              <div style={{ padding: "1rem 0" }}>
                {!demoInvoiceUploaded && !demoUploading && (
                  <div className="upload-dropzone" onClick={handleDemoUpload}>
                    <div className="upload-icon">📄</div>
                    <div className="upload-text">Drag and drop your utility bill or invoice</div>
                    <div className="upload-subtext">Supports PDF, XLSX, and CSV. We will extract the consumption data to calculate your ESG carbon emissions.</div>
                    <button className="btn-secondary" style={{ marginTop: "1rem", pointerEvents: "none" }}>Browse Files</button>
                  </div>
                )}
                
                {demoUploading && (
                  <div className="calculating-state">
                    <div className="spinner"></div>
                    <h3 style={{ color: "white", marginBottom: "0.5rem" }}>Extracting data...</h3>
                    <p style={{ color: "rgba(255,255,255,0.5)" }}>Running AI models to map consumption to emission factors</p>
                  </div>
                )}

                {demoInvoiceUploaded && !demoUploading && (
                  <div className="dashboard-results">
                    {/* Left side: PDF Preview */}
                    <div className="pdf-preview">
                      <div className="pdf-header">
                        <div>
                          <div className="pdf-title">ESG Carbon Emission Report</div>
                          <div className="pdf-meta">Generated for India Operations • 2024 Audit</div>
                        </div>
                        <div style={{ background: "rgba(184,255,61,0.2)", color: "#4d7c0f", padding: "0.25rem 0.75rem", borderRadius: "99px", fontSize: "0.75rem", fontWeight: 700 }}>VERIFIED</div>
                      </div>
                      <div className="pdf-content-skeleton">
                        <div className="pdf-line medium"></div>
                        <div className="pdf-line short"></div>
                        <div className="pdf-box">
                          <div className="pdf-line" style={{ marginBottom: "0.5rem" }}></div>
                          <div className="pdf-line" style={{ width: "90%" }}></div>
                        </div>
                        <div className="pdf-line medium"></div>
                        <div className="pdf-line"></div>
                        <div className="pdf-line short"></div>
                      </div>
                      <button className="download-btn" onClick={() => showToast("PDF Download started!")}>
                        📥 Download PDF
                      </button>
                    </div>

                    {/* Right side: Calculations */}
                    <div>
                      <h3 style={{ color: "white", marginBottom: "1.5rem", fontSize: "1.25rem" }}>Emission Calculations</h3>
                      {data.calculatedStats.map(stat => (
                        <div className="scope-card" key={stat.label}>
                          <div className="scope-header">
                            <span className="scope-label">{stat.label}</span>
                            <span className="scope-value">{stat.value}</span>
                          </div>
                          <div className="scope-progress-bg">
                            <div className="scope-progress-fill" style={{ width: `${stat.percent}%`, background: stat.label.includes("Scope 1") ? "var(--lime)" : stat.label.includes("Scope 2") ? "#2E5BFF" : "#FFB020" }}></div>
                          </div>
                          <div className="scope-desc">Major driver: {stat.scope}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "Analytics" && (
              <div style={{ padding: "1rem 0" }}>
                {!demoInvoiceUploaded ? (
                  <div className="empty-state">
                    <div style={{ fontSize: "3rem", marginBottom: "1rem", opacity: 0.5 }}>📊</div>
                    <h3 style={{ color: "white", marginBottom: "0.5rem" }}>No data to analyze</h3>
                    <p style={{ color: "rgba(255,255,255,0.5)" }}>Please upload an invoice in the Dashboard tab to see analytics.</p>
                  </div>
                ) : (
                  <>
                    <h3 style={{ color: "white", marginBottom: "1.5rem", fontSize: "1.25rem" }}>Historical Emissions by Scope</h3>
                    
                    {/* Line Chart SVG */}
                    <div className="demo-chart-line-wrap" style={{ position: "relative", height: "250px", marginBottom: "3rem", paddingBottom: "1.5rem", borderBottom: "1px solid rgba(255,255,255,0.1)", marginTop: "2.5rem" }}>
                      {/* Legend */}
                      <div style={{ position: "absolute", top: "-2rem", right: "0", display: "flex", gap: "1.25rem", fontSize: "0.75rem", color: "rgba(255,255,255,0.7)" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--lime)" }}></span> Scope 1</span>
                        <span style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#2E5BFF" }}></span> Scope 2</span>
                        <span style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#FFB020" }}></span> Scope 3</span>
                      </div>

                      <svg viewBox="0 0 1000 200" style={{ width: "100%", height: "100%", overflow: "visible" }}>
                        {/* Grid lines */}
                        {[0, 1, 2, 3, 4].map(line => (
                          <line key={line} x1="0" y1={line * 50} x2="1000" y2={line * 50} stroke="rgba(255,255,255,0.06)" strokeDasharray="4 4" />
                        ))}
                        
                        {/* Scope 1 Line */}
                        <path 
                          d={data.historyBars.map((b, i) => `${i === 0 ? 'M' : 'L'} ${(i / 11) * 1000} ${200 - (b.s1 / 40) * 180}`).join(" ")}
                          fill="none" stroke="var(--lime)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" 
                        />
                        {/* Scope 2 Line */}
                        <path 
                          d={data.historyBars.map((b, i) => `${i === 0 ? 'M' : 'L'} ${(i / 11) * 1000} ${200 - (b.s2 / 40) * 180}`).join(" ")}
                          fill="none" stroke="#2E5BFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" 
                        />
                        {/* Scope 3 Line */}
                        <path 
                          d={data.historyBars.map((b, i) => `${i === 0 ? 'M' : 'L'} ${(i / 11) * 1000} ${200 - (b.s3 / 40) * 180}`).join(" ")}
                          fill="none" stroke="#FFB020" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" 
                        />
                        
                        {/* Data points */}
                        {data.historyBars.map((b, i) => (
                          <g key={i}>
                            <circle cx={(i / 11) * 1000} cy={200 - (b.s1 / 40) * 180} r="4" fill="#111827" stroke="var(--lime)" strokeWidth="2" />
                            <circle cx={(i / 11) * 1000} cy={200 - (b.s2 / 40) * 180} r="4" fill="#111827" stroke="#2E5BFF" strokeWidth="2" />
                            <circle cx={(i / 11) * 1000} cy={200 - (b.s3 / 40) * 180} r="4" fill="#111827" stroke="#FFB020" strokeWidth="2" />
                          </g>
                        ))}
                      </svg>

                      {/* X-axis Labels */}
                      <div style={{ position: "relative", height: "1.5rem" }}>
                        {data.historyLabels.map((lbl, i) => (
                          <span key={i} style={{ position: "absolute", left: `${(i / 11) * 100}%`, transform: `translateX(${i === 0 ? '0' : i === 11 ? '-100%' : '-50%'})`, color: "rgba(255,255,255,0.4)", fontSize: "0.625rem", bottom: "-1.5rem" }}>{lbl}</span>
                        ))}
                      </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2rem" }}>
                      {/* Left: Overall Scope Breakdown */}
                      <div style={{ background: "rgba(255,255,255,0.02)", padding: "1.5rem", borderRadius: "16px", border: "1px solid rgba(255,255,255,0.05)" }}>
                        <h4 style={{ color: "white", fontSize: "1rem", marginBottom: "1.5rem" }}>Overall Breakdown</h4>
                        {data.scopeBreakdown.map(sb => (
                          <div key={sb.name} style={{ marginBottom: "1rem" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.875rem", marginBottom: "0.5rem" }}>
                              <span style={{ color: "rgba(255,255,255,0.7)" }}>{sb.name}</span>
                              <span style={{ color: "white", fontWeight: 600 }}>{sb.percent}%</span>
                            </div>
                            <div style={{ height: "6px", background: "rgba(255,255,255,0.1)", borderRadius: "3px", overflow: "hidden" }}>
                              <div style={{ width: `${sb.percent}%`, height: "100%", background: sb.color, borderRadius: "3px" }}></div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Right: Top Sources List */}
                      <div style={{ background: "rgba(255,255,255,0.02)", padding: "1.5rem", borderRadius: "16px", border: "1px solid rgba(255,255,255,0.05)" }}>
                        <h4 style={{ color: "white", fontSize: "1rem", marginBottom: "1.5rem" }}>Top Emission Sources</h4>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          {data.topSources.map((ts, idx) => (
                            <div key={ts.name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "0.85rem", marginBottom: "0.85rem", borderBottom: idx === data.topSources.length - 1 ? "none" : "1px solid rgba(255,255,255,0.05)" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                                <div style={{ width: 8, height: 8, background: idx === 0 ? "var(--lime)" : "rgba(255,255,255,0.2)", borderRadius: "50%" }}></div>
                                <span style={{ color: "rgba(255,255,255,0.8)", fontSize: "0.875rem" }}>{ts.name}</span>
                              </div>
                              <span style={{ color: idx === 0 ? "var(--lime)" : "white", fontSize: "0.875rem", fontFamily: "'IBM Plex Mono', monospace" }}>{ts.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === "Insights" && (
              <div style={{ padding: "1rem 0" }} key="insights-tab">
                {!demoInvoiceUploaded ? (
                  <div className="empty-state">
                    <div style={{ fontSize: "3rem", marginBottom: "1rem", opacity: 0.5 }}>💡</div>
                    <h3 style={{ color: "white", marginBottom: "0.5rem" }}>Unlock AI Insights</h3>
                    <p style={{ color: "rgba(255,255,255,0.5)" }}>Upload your data in Dashboard to get automated reduction strategies.</p>
                  </div>
                ) : (
                  <>
                    <h3 style={{ color: "white", marginBottom: "1.5rem", fontSize: "1.25rem" }}>Recommended Strategies</h3>
                    {data.strategies.map((strategy, i) => (
                      <div className="insight-card" key={i} style={{ animationDelay: `${i * 0.15}s` }}>
                        <div className="insight-icon">{strategy.icon}</div>
                        <div className="insight-content">
                          <h4>{strategy.title}</h4>
                          <p>{strategy.description}</p>
                          <div className="insight-footer">
                            <span className="insight-impact">Impact: {strategy.impact}</span>
                            <span className="insight-difficulty">Implementation: {strategy.difficulty}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}

            <div style={{ marginTop: "1.5rem", display: "flex", justifyContent: "center" }}>
              <button className="btn-primary" onClick={() => setShowDemoModal(true)}>Get full access →</button>
            </div>
          </div>
        </div>
      </section>


      {/* ── CONTACT US SECTION (v2) ── */}
      <section id="contact-us" className="contact-v2-section">
        <div className="contact-v2-inner">
          {/* Header */}
          <div className="contact-v2-header reveal">
            <span className="title-pill" style={{ marginBottom: "1.25rem", display: "inline-flex" }}>
              <span style={{ width: 6, height: 6, background: "var(--lime)", borderRadius: "50%", display: "inline-block" }} />
              Contact Us
            </span>
            <h2>Let's talk carbon. 🌿</h2>
            <p>Project developer, corporate buyer, or integration partner — our team responds within one business day.</p>
          </div>

          <div className="contact-v2-grid">
            {/* Left: info cards */}
            <div className="contact-v2-info">
              {[
                {
                  icon: "📧",
                  title: "Email us",
                  desc: "For general enquiries and partnership discussions.",
                  link: "hello@carbonsync.io",
                  href: "mailto:hello@carbonsync.io",
                },
                {
                  icon: "📅",
                  title: "Book a call",
                  desc: "30-minute discovery call with our solutions team. Available Mon–Fri, 9 AM – 6 PM IST.",
                  link: "Schedule on Calendly →",
                  href: "https://calendly.com",
                  target: "_blank",
                },
                {
                  icon: "🏢",
                  title: "Our office",
                  desc: "CarbonSync HQ · Koramangala, Bengaluru, Karnataka 560034, India.",
                  link: "Get directions →",
                  href: "https://maps.google.com/?q=Koramangala,Bengaluru,Karnataka,560034",
                  target: "_blank",
                },
                {
                  icon: "🛠️",
                  title: "Technical support",
                  desc: "API issues, integration help, or platform bugs — we're fast.",
                  link: "support@carbonsync.io",
                  href: "mailto:support@carbonsync.io",
                },
              ].map(({ icon, title, desc, link, href, target }, i) => (
                <div className={`contact-info-card reveal td-${i}`} key={title}>
                  <div className="contact-info-icon">{icon}</div>
                  <div>
                    <div className="contact-info-title">{title}</div>
                    <div className="contact-info-desc">{desc}</div>
                    <a href={href} target={target} rel={target === "_blank" ? "noopener noreferrer" : undefined} className="contact-info-link">{link}</a>
                  </div>
                </div>
              ))}

              {/* Social bar */}
              <div className="contact-social-bar reveal td-4">
                <div className="contact-social-label">Find us online</div>
                <div className="contact-social-links">
                  {[
                    { icon: "𝕏", label: "Twitter / X", href: "https://twitter.com", target: "_blank" },
                    { icon: "in", label: "LinkedIn", href: "https://linkedin.com", target: "_blank" },
                    { icon: "◎", label: "GitHub", href: "https://github.com", target: "_blank" },
                    { icon: "✉", label: "Newsletter", href: "#contact" },
                  ].map(({ icon, label, href, target }) => (
                    <a key={label} href={href} target={target} rel={target === "_blank" ? "noopener noreferrer" : undefined} onClick={href === "#contact" ? (e) => handleNavClick(e, "#contact") : undefined} className="contact-social-link">
                      <span style={{ fontWeight: 700 }}>{icon}</span> {label}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: form */}
            <div className="contact-v2-form-wrap reveal-right td-1">
              {contactSent ? (
                <div className="contact-v2-success">
                  <div className="contact-v2-success-icon">✅</div>
                  <h3>Message received!</h3>
                  <p>Our team will get back to you within one business day. Check your inbox for a confirmation.</p>
                  <button className="btn-primary" style={{ marginTop: "0.5rem" }} onClick={() => { setContactSent(false); setContactChips([]); setContactForm({ name: "", email: "", company: "", type: "", message: "", phone: "" }); }}>
                    Send another →
                  </button>
                </div>
              ) : (
                <>
                  <div className="contact-v2-form-title">Send us a message</div>
                  <div className="contact-v2-form-subtitle">Tell us what you're working on and we'll find the best path forward.</div>

                  {/* Tab switcher */}
                  <div className="contact-v2-tabs">
                    {["Message", "Partnership", "Press"].map(tab => (
                      <button
                        key={tab}
                        className={`contact-v2-tab${contactTab === tab ? " active" : ""}`}
                        onClick={() => setContactTab(tab)}
                      >
                        {tab === "Message" ? "💬 " : tab === "Partnership" ? "🤝 " : "📰 "}{tab}
                      </button>
                    ))}
                  </div>

                  <div className="contact-v2-field-row">
                    <div className="contact-v2-field">
                      <label>Full name *</label>
                      <input placeholder="Priya Sharma" value={contactForm.name} onChange={e => setContactForm({ ...contactForm, name: e.target.value })} />
                    </div>
                    <div className="contact-v2-field">
                      <label>Work email *</label>
                      <input type="email" placeholder="priya@company.com" value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} />
                    </div>
                  </div>

                  <div className="contact-v2-field-row">
                    <div className="contact-v2-field">
                      <label>Company</label>
                      <input placeholder="Your company" value={contactForm.company} onChange={e => setContactForm({ ...contactForm, company: e.target.value })} />
                    </div>
                    <div className="contact-v2-field">
                      <label>Phone (optional)</label>
                      <input placeholder="+91 98765 43210" value={contactForm.phone} onChange={e => setContactForm({ ...contactForm, phone: e.target.value })} />
                    </div>
                  </div>

                  {/* Dynamic chips based on tab */}
                  <div className="contact-v2-field">
                    <label>
                      {contactTab === "Message" ? "I'm interested in" : contactTab === "Partnership" ? "Partnership type" : "Media enquiry"}
                    </label>
                    <div className="contact-v2-chip-group">
                      {(contactTab === "Message"
                        ? ["Carbon Registry", "Marketplace", "Compliance Suite", "API & Integrations", "Pricing", "Other"]
                        : contactTab === "Partnership"
                          ? ["ACVA / Validator", "Data Provider", "ERP Integration", "Reseller", "Registry Partner"]
                          : ["Press Interview", "Product Review", "Data Request", "Speaking Invite", "Guest Post"]
                      ).map(chip => (
                        <button
                          key={chip}
                          className={`contact-v2-chip${contactChips.includes(chip) ? " selected" : ""}`}
                          onClick={() => toggleChip(chip)}
                        >{chip}</button>
                      ))}
                    </div>
                  </div>

                  <div className="contact-v2-field">
                    <label>Message *</label>
                    <textarea
                      placeholder={
                        contactTab === "Message"
                          ? "Tell us what you're working on, what you need, and the best time to connect…"
                          : contactTab === "Partnership"
                            ? "Describe the partnership opportunity and what you're hoping to achieve together…"
                            : "Share your publication, audience size, and what you'd like to cover about CarbonSync…"
                      }
                      value={contactForm.message}
                      onChange={e => setContactForm({ ...contactForm, message: e.target.value })}
                    />
                  </div>

                  <button className="contact-v2-submit" onClick={handleContactSubmit}>
                    <span>Send message</span>
                    <span style={{ fontSize: "1.1rem" }}>✈</span>
                  </button>

                  <div style={{ marginTop: "1rem", textAlign: "center", fontSize: "0.75rem", color: "var(--text-secondary)" }}>
                    🔒 Your information is never shared or sold. View our <a href="#" style={{ color: "#667eea" }}>Privacy Policy</a>.
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── FEEDBACK SECTION ── */}
      <section className="feedback-section" id="feedback">
        <div className="feedback-inner">
          <div className="feedback-header">
            <span className="title-pill-dark" style={{ marginBottom: "1rem", display: "inline-flex" }}>
              <span style={{ width: 6, height: 6, background: "var(--lime)", borderRadius: "50%", display: "inline-block" }} />
              Community Feedback
            </span>
            <h2>Help us build better.</h2>
            <p>Your feedback directly shapes the product. We read every submission.</p>
          </div>
          <div className="feedback-grid">
            <div className="feedback-form-wrap">
              {feedbackSent ? (
                <div style={{ textAlign: "center", padding: "2rem 0", display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem" }}>
                  <div style={{ fontSize: "3rem" }}>💚</div>
                  <h3 style={{ color: "white", fontSize: "1.5rem" }}>Thank you!</h3>
                  <p style={{ color: "rgba(255,255,255,0.6)", lineHeight: 1.7 }}>Your feedback has been recorded. We appreciate you helping us improve CarbonSync.</p>
                  <button className="btn-primary" onClick={() => setFeedbackSent(false)}>Submit more feedback</button>
                </div>
              ) : (
                <>
                  <h3>Share your experience</h3>
                  <div className="feedback-field">
                    <label>Overall rating</label>
                    <div className="rating-group">
                      {["😞", "😐", "🙂", "😊", "🤩"].map((emoji, i) => (
                        <button key={i} className={`rating-btn${feedbackForm.rating === String(i + 1) ? " selected" : ""}`} onClick={() => setFeedbackForm({ ...feedbackForm, rating: String(i + 1) })}>
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="feedback-field">
                    <label>Category</label>
                    <select value={feedbackForm.category} onChange={e => setFeedbackForm({ ...feedbackForm, category: e.target.value })}>
                      <option value="">What's your feedback about?</option>
                      <option>Registry &amp; Ledger</option>
                      <option>Marketplace</option>
                      <option>Compliance &amp; Reports</option>
                      <option>Onboarding Experience</option>
                      <option>API &amp; Integrations</option>
                      <option>General Suggestion</option>
                    </select>
                  </div>
                  <div className="feedback-field">
                    <label>Email (optional)</label>
                    <input type="email" placeholder="For follow-up if needed" value={feedbackForm.email} onChange={e => setFeedbackForm({ ...feedbackForm, email: e.target.value })} />
                  </div>
                  <div className="feedback-field">
                    <label>Your feedback *</label>
                    <textarea placeholder="Tell us what you think, what's working, what could be better..." value={feedbackForm.message} onChange={e => setFeedbackForm({ ...feedbackForm, message: e.target.value })} required />
                  </div>
                  <button className="feedback-submit" onClick={handleFeedbackSubmit}>Submit feedback →</button>
                </>
              )}
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact">
        <div className="footer-accent" />
        <div className="footer-grid">
          <div className="footer-form">
            <h3>Stay updated</h3>
            <p>Subscribe to CarbonSync updates, market insights, and compliance news.</p>
            <form onSubmit={async (e) => {
              e.preventDefault();
              await fetch("https://formspree.io/f/your_form_id", {
                method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" },
                body: JSON.stringify({ formType: "Newsletter Subscription", email: e.target.elements[0].value })
              }).catch(() => { });
              showToast("📬 Subscribed! Welcome to CarbonSync.");
              e.target.reset();
            }}>
              <div className="form-group">
                <input type="email" placeholder="Your email address" required />
              </div>
              <button type="submit" className="btn-primary" style={{ width: "100%", justifyContent: "center" }}>
                Subscribe ✈
              </button>
            </form>
          </div>
          <div className="footer-info">
            <h4>CarbonSync</h4>
            <p>Carbon accounting platform that converts business data into audit-ready emissions reports and actionable reduction insights.</p>
            <div className="footer-links">
              <a href="#" className="footer-link">🔒 Privacy</a>
              <a href="#" className="footer-link">📄 Terms</a>
              <a href="#" className="footer-link">🛡️ Security</a>
              <a href="#" className="footer-link">📖 Methodology</a>
            </div>
            <div className="footer-copyright">© 2026 CarbonSync. All rights reserved.</div>
          </div>
        </div>
      </footer>

      {/* Toast Notification */}
      {toast && (
        <div className="success-toast">
          <span className="toast-icon">✅</span>
          <span className="toast-msg">{toast}</span>
        </div>
      )}
    </>
  );
}
